import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useToast } from '@shared/hooks/use-toast';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnblockBatch } from '../../../hooks/useBatchMutations';
import type { Batch } from '../../../models/Batch';

interface ExtendedModalCenterRef extends ModalCenterRef {
  openWithBatch: (batch: Batch) => void;
}

const UnblockBatchModal = forwardRef<ExtendedModalCenterRef, object>((_, ref) => {
  const { t } = useTranslation('batchesModule');
  const { toast } = useToast();
  const unblockBatch = useUnblockBatch();
  const internalModalRef = useRef<ModalCenterRef>(null);

  const [batch, setBatch] = useState<Batch | null>(null);

  const handleCancel = () => {
    internalModalRef.current?.closeModal();
    setBatch(null);
  };

  const handleSubmit = async () => {
    if (!batch) return;

    try {
      await unblockBatch.mutateAsync(batch.id);
      toast({ title: t('toast.unblockSuccess'), variant: 'default' });
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('toast.unblockError'),
        description: error?.response?.data?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const openWithBatch = useCallback((batchData: Batch) => {
    setBatch(batchData);
    internalModalRef.current?.openModal();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: () => internalModalRef.current?.openModal(),
      closeModal: () => internalModalRef.current?.closeModal(),
      openWithBatch,
    }),
    [openWithBatch],
  );

  return (
    <ModalCenter ref={internalModalRef} title={t('modals.unblock.title')} type="success">
      <div className="space-y-4">
        <p className="text-sm text-text">{t('modals.unblock.message', { batchNumber: batch?.batch_number })}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="lg" onClick={handleCancel} disabled={unblockBatch.isPending}>
            {t('modals.unblock.cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="text-white"
            onClick={handleSubmit}
            disabled={unblockBatch.isPending}
          >
            {t('modals.unblock.submit')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

UnblockBatchModal.displayName = 'UnblockBatchModal';

export default UnblockBatchModal;
